if (!customElements.get("immersive-feature-carousel")) {
  /**
   * Horizontal feature carousel.
   * First slide pins to the start inset, last slide pins to the end inset,
   * and every other active slide is centered in the viewport.
   */
  class ImmersiveFeatureCarousel extends HTMLElement {
    static #minDuration = 480;
    static #maxDuration = 720;

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector("[data-carousel-viewport]");
      this.track = this.querySelector("[data-carousel-track]");
      this.slides = Array.from(this.querySelectorAll("[data-carousel-slide]"));
      this.previousButton = this.querySelector("[data-carousel-previous]");
      this.nextButton = this.querySelector("[data-carousel-next]");
      this.activeCopy = this.querySelector("[data-carousel-active-copy]");
      this.currentIndex = Math.min(
        Math.max(Number.parseInt(this.dataset.initialSlide || "0", 10), 0),
        Math.max(0, this.slides.length - 1)
      );
      this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.nonMobileLayoutQuery = window.matchMedia("(min-width: 768px)");
      this.scrollPinLayoutQuery = window.matchMedia("(min-width: 1280px)");
      this.autoplayEnabled = this.dataset.autoplay === "true" && !this.reducedMotionQuery.matches;
      this.scrollPinEnabled = this.dataset.scrollPin === "true";
      this.scrollStepWindow = Math.max(300, Number.parseInt(this.dataset.scrollStepWindow || "800", 10));
      this.scrollStepLimit = Math.max(1, Number.parseInt(this.dataset.scrollStepLimit || "1", 10));
      this.autoplayDelay = Math.max(3000, Number.parseInt(this.dataset.autoplayDelay || "5000", 10));
      this.pointerStartX = 0;
      this.pointerStartScroll = 0;
      this.pointerId = null;
      this.isDragging = false;
      this.isAnimating = false;
      this.scrollFrame = 0;
      this.pinScrollFrame = 0;
      this.pinLastObservedScrollY = window.scrollY;
      this.pinInternalTarget = null;
      this.pinApproach = 0;
      this.wheelDelta = 0;
      this.wheelLastStepAt = 0;
      this.wheelLockUntil = 0;
      this.wheelResetTimer = 0;
      this.resizeFrame = 0;
      this.entranceFrame = 0;
      this.scrollAnimation = 0;
      this.copyMeasure = null;
      this.autoplayResumeTimer = 0;
      this.entranceToken = 0;
      this.entranceSlide = null;
      this.isInViewport = false;
      this.revealRoot = this.closest("section-entrance-reveal");
      this.sectionRoot = this.closest(".immersive-feature-carousel");
      this.footer = this.querySelector(".immersive-feature-carousel__footer");

      const signal = this.abortController.signal;
      this.previousButton?.addEventListener("click", () => this.navigateTo(this.currentIndex - 1), { signal });
      this.nextButton?.addEventListener("click", () => this.navigateTo(this.currentIndex + 1), { signal });
      this.addEventListener("keydown", (event) => this.handleKeydown(event), { signal });
      this.viewport?.addEventListener("scroll", () => this.handleScroll(), { signal, passive: true });
      this.viewport?.addEventListener("pointerdown", (event) => this.handlePointerDown(event), { signal });
      this.viewport?.addEventListener("pointermove", (event) => this.handlePointerMove(event), { signal });
      this.viewport?.addEventListener("pointerup", (event) => this.handlePointerUp(event), { signal });
      this.viewport?.addEventListener("pointercancel", (event) => this.handlePointerCancel(event), { signal });
      this.viewport?.addEventListener("lostpointercapture", (event) => this.handleLostPointerCapture(event), { signal });
      this.addEventListener("mouseenter", () => this.pauseAutoplay(), { signal });
      this.addEventListener("mouseleave", () => this.deferAutoplayResume(), { signal });
      this.addEventListener("focusin", () => this.pauseAutoplay(), { signal });
      this.addEventListener("focusout", () => this.deferAutoplayResume(), { signal });
      this.addEventListener("pointerdown", () => this.pauseAutoplay(), { signal });
      window.addEventListener("resize", () => this.handleResize(), { signal, passive: true });
      window.addEventListener("scroll", () => this.handlePinScroll(), { signal, passive: true });
      window.addEventListener("wheel", (event) => this.handlePinWheel(event), { signal, passive: false });
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.pauseAutoplay();
        else this.resumeAutoplay();
      }, { signal });

      if ("IntersectionObserver" in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.isInViewport = entry?.isIntersecting ?? false;
          if (this.isInViewport) {
            if (!this.usesEntranceReveal()) this.playEntrance(this.slides[this.currentIndex]);
            this.resumeAutoplay();
            return;
          }
          if (!this.usesEntranceReveal()) this.resetEntrance();
          this.pauseAutoplay();
        }, { threshold: 0.15 });
        this.intersectionObserver.observe(this);
      }

      this.revealRoot?.addEventListener("theme-entrance-reveal:change", (event) => {
        this.handleRevealChange(event);
      }, { signal });

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        if (this.viewport) this.resizeObserver.observe(this.viewport);
      }

      this.bindMediaQuery(this.reducedMotionQuery, () => {
        this.autoplayEnabled = this.dataset.autoplay === "true" && !this.reducedMotionQuery.matches;
        if (this.reducedMotionQuery.matches) this.pauseAutoplay();
        this.syncScrollPinMode();
      }, signal);
      this.bindMediaQuery(this.nonMobileLayoutQuery, () => {
        this.syncCopyHeight();
        this.goTo(this.currentIndex, false);
      }, signal);
      this.bindMediaQuery(this.scrollPinLayoutQuery, () => {
        this.syncLayoutSemantics();
        this.syncScrollPinMode();
        this.goTo(this.currentIndex, false);
      }, signal);

      if (window.Shopify?.designMode) {
        document.addEventListener("shopify:block:select", (event) => this.handleBlockSelect(event), { signal });
      }

      this.classList.add("is-ready");
      this.syncLayoutSemantics();
      this.syncCopyHeight();
      this.syncScrollPinMode();
      this.goTo(this.currentIndex, false);
      document.fonts?.ready.then(() => {
        if (!this.abortController) return;
        this.syncCopyHeight();
      });
      this.resumeAutoplay();
    }

    disconnectedCallback() {
      this.entranceToken += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.stopScrollAnimation();
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.autoplayResumeTimer);
      clearTimeout(this.wheelResetTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.pinScrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.entranceFrame);
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.copyMeasure?.remove();
      this.copyMeasure = null;
      this.style.removeProperty("--immersive-feature-carousel-sticky-media-clip");
      this.style.removeProperty("--immersive-feature-carousel-sticky-media-height");
      this.revealRoot?.classList.remove("is-scroll-pin-ready");
      this.sectionRoot?.classList.remove("is-sticky-active", "is-sticky-content-overflowing");
      this.viewport?.classList.remove("is-dragging", "is-animating");
    }

    /**
     * @param {MediaQueryList} mediaQuery
     * @param {() => void} handler
     * @param {AbortSignal} signal
     */
    bindMediaQuery(mediaQuery, handler, signal) {
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handler, { signal });
        return;
      }
      mediaQuery.addListener(handler);
      signal.addEventListener("abort", () => mediaQuery.removeListener(handler), { once: true });
    }

    handleKeydown(event) {
      if (!this.isInteractiveLayout()) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.target instanceof HTMLElement && event.target.closest("a, input, textarea, select")) return;
      event.preventDefault();
      this.navigateTo(this.currentIndex + (event.key === "ArrowRight" ? 1 : -1));
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const slide = event.target.matches("[data-carousel-slide]")
        ? event.target
        : event.target.querySelector("[data-carousel-slide]");
      const index = slide ? this.slides.indexOf(slide) : -1;
      if (index >= 0) this.goTo(index, false);
    }

    handlePointerDown(event) {
      if (!this.isInteractiveLayout()) return;
      if (!event.isPrimary || event.button !== 0 || !this.viewport || this.slides.length < 2) return;
      if (event.target instanceof Element && event.target.closest("a, button, input, textarea, select")) return;

      this.stopScrollAnimation();
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartScroll = this.viewport.scrollLeft;
      this.isDragging = false;
      this.viewport.setPointerCapture(event.pointerId);
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId || !this.viewport) return;
      const distance = event.clientX - this.pointerStartX;
      if (Math.abs(distance) > 4) {
        this.isDragging = true;
        this.viewport.classList.add("is-dragging");
      }
      if (this.isDragging) {
        this.viewport.scrollLeft = this.clampScroll(this.pointerStartScroll - distance);
      }
    }

    handlePointerUp(event) {
      if (event.pointerId !== this.pointerId || !this.viewport) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerId = null;
      if (this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      const swipeThreshold = Math.min(72, this.viewport.clientWidth * 0.12);
      if (this.isDragging && Math.abs(distance) > swipeThreshold) {
        this.navigateTo(this.currentIndex + (distance < 0 ? 1 : -1));
      } else {
        this.navigateTo(this.nearestIndex());
      }

      this.isDragging = false;
      this.viewport.classList.remove("is-dragging");
      this.resumeAutoplay();
    }

    handleLostPointerCapture(event) {
      if (event.pointerId !== this.pointerId || !this.viewport) return;

      this.pointerId = null;
      this.isDragging = false;
      this.viewport.classList.remove("is-dragging");
      this.navigateTo(this.nearestIndex());
      this.resumeAutoplay();
    }

    handlePointerCancel(event) {
      if (event.pointerId !== this.pointerId || !this.viewport) return;

      this.pointerId = null;
      this.isDragging = false;
      this.viewport.classList.remove("is-dragging");
      this.navigateTo(this.nearestIndex());
      this.resumeAutoplay();
    }

    handleResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.syncCopyHeight();
        this.goTo(this.currentIndex, false);
        this.handlePinScroll();
      });
    }

    isScrollPinActive() {
      return this.scrollPinEnabled
        && this.scrollPinLayoutQuery.matches
        && !this.prefersReducedMotion()
        && this.slides.length > 1;
    }

    isInteractiveLayout() {
      return this.scrollPinLayoutQuery?.matches ?? window.matchMedia("(min-width: 1280px)").matches;
    }

    syncLayoutSemantics() {
      if (this.isInteractiveLayout()) {
        this.tabIndex = 0;
        this.setAttribute("aria-roledescription", "carousel");
        return;
      }

      this.removeAttribute("tabindex");
      this.removeAttribute("aria-roledescription");
    }

    syncScrollPinMode() {
      const active = this.isScrollPinActive();
      this.revealRoot?.classList.toggle("is-scroll-pin-ready", active);
      if (!active) {
        this.sectionRoot?.classList.remove("is-sticky-active", "is-sticky-content-overflowing");
        this.resetPinWheelState();
        this.pinInternalTarget = null;
        return;
      }

      this.syncStickyContentFit();
      this.pinLastObservedScrollY = window.scrollY;
      const bounds = this.pinnedScrollBounds();
      if (bounds) {
        const scrollEnd = bounds.start + bounds.distance;
        if (window.scrollY < bounds.start) this.pinApproach = -1;
        else if (window.scrollY > scrollEnd) this.pinApproach = 1;
      }
      this.pauseAutoplay();
      this.handlePinScroll();
    }

    resetPinWheelState() {
      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = 0;
      this.wheelDelta = 0;
      this.wheelLastStepAt = 0;
      this.wheelLockUntil = 0;
    }

    pinnedScrollBounds() {
      if (!this.revealRoot) return null;
      const revealRect = this.revealRoot.getBoundingClientRect();
      const stickyOffset = Number.parseFloat(getComputedStyle(this).insetBlockStart) || 0;
      const start = window.scrollY + revealRect.top - stickyOffset;
      const distance = Math.max(0, this.revealRoot.offsetHeight - this.offsetHeight);
      return { start, distance };
    }

    handlePinScroll() {
      if (!this.isScrollPinActive()) return;
      cancelAnimationFrame(this.pinScrollFrame);
      this.pinScrollFrame = requestAnimationFrame(() => {
        const bounds = this.pinnedScrollBounds();
        if (!bounds || bounds.distance <= 0) return;
        const currentScrollY = window.scrollY;
        const scrollEnd = bounds.start + bounds.distance;
        this.syncStickyActiveState(currentScrollY, bounds);

        // A navigation control can jump across this section to a later module.
        // Do not reinterpret that programmatic jump as a user entering the
        // pinned carousel, otherwise the carousel pulls the page back to its
        // first slide before the requested destination is reached.
        if (Math.abs(currentScrollY - this.pinLastObservedScrollY) > window.innerHeight) {
          this.pinApproach = currentScrollY < bounds.start ? -1 : currentScrollY > scrollEnd ? 1 : 0;
          this.pinLastObservedScrollY = currentScrollY;
          return;
        }

        this.pinLastObservedScrollY = currentScrollY;

        if (this.pinInternalTarget !== null && Math.abs(currentScrollY - this.pinInternalTarget) <= 2) {
          this.pinInternalTarget = null;
          return;
        }

        const now = performance.now();
        if (this.pinApproach < 0 && currentScrollY >= bounds.start) {
          this.capturePinScrollEntry(0, bounds.start, now);
          return;
        }

        if (this.pinApproach > 0 && currentScrollY <= scrollEnd) {
          this.capturePinScrollEntry(this.slides.length - 1, scrollEnd, now);
          return;
        }

        if (currentScrollY < bounds.start - 1) {
          this.pinApproach = -1;
          return;
        }

        if (currentScrollY > scrollEnd + 1) {
          this.pinApproach = 1;
          return;
        }

        this.scrollToPinnedIndex(this.currentIndex);
      });
    }

    capturePinScrollEntry(index, scrollTop, timestamp) {
      const contentDoesNotFit = this.sectionRoot?.classList.contains("is-sticky-content-overflowing") ?? false;
      this.sectionRoot?.classList.toggle(
        "is-sticky-active",
        contentDoesNotFit
      );
      this.wheelDelta = 0;
      this.wheelLastStepAt = timestamp;
      this.wheelLockUntil = timestamp + this.scrollStepWindow;
      this.pinApproach = 0;
      this.goTo(index, false);
      this.pinInternalTarget = scrollTop;
      window.scrollTo({ top: scrollTop, behavior: "auto" });
    }

    handlePinWheel(event) {
      if (!this.isScrollPinActive() || event.ctrlKey) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const bounds = this.pinnedScrollBounds();
      if (!bounds || bounds.distance <= 0) return;
      const scrollEnd = bounds.start + bounds.distance;
      const now = performance.now();
      const direction = event.deltaY > 0 ? 1 : -1;
      const deltaScale = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? window.innerHeight
          : 1;
      const scaledDelta = event.deltaY * deltaScale;

      if (this.pinApproach < 0 && window.scrollY >= bounds.start - 1) {
        event.preventDefault();
        this.capturePinScrollEntry(0, bounds.start, now);
        return;
      }

      if (this.pinApproach > 0 && window.scrollY <= scrollEnd + 1) {
        event.preventDefault();
        this.capturePinScrollEntry(this.slides.length - 1, scrollEnd, now);
        return;
      }

      if (this.pinLastObservedScrollY < bounds.start && window.scrollY >= bounds.start) {
        event.preventDefault();
        this.capturePinScrollEntry(0, bounds.start, now);
        return;
      }

      if (this.pinLastObservedScrollY > scrollEnd && window.scrollY <= scrollEnd) {
        event.preventDefault();
        this.capturePinScrollEntry(this.slides.length - 1, scrollEnd, now);
        return;
      }

      if (window.scrollY < bounds.start - 1) {
        if (direction < 0 || window.scrollY + scaledDelta < bounds.start) return;
        event.preventDefault();
        this.capturePinScrollEntry(0, bounds.start, now);
        return;
      }

      if (window.scrollY > scrollEnd + 1) {
        if (direction > 0 || window.scrollY + scaledDelta > scrollEnd) return;
        event.preventDefault();
        this.capturePinScrollEntry(this.slides.length - 1, scrollEnd, now);
        return;
      }

      const isCoolingDown = now < this.wheelLockUntil;
      const atBoundary = direction > 0
        ? this.currentIndex >= this.slides.length - 1
        : this.currentIndex <= 0;
      if (atBoundary && !isCoolingDown) {
        this.wheelDelta = 0;
        return;
      }

      event.preventDefault();
      this.wheelDelta += scaledDelta;

      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = window.setTimeout(() => {
        this.wheelDelta = 0;
      }, 140);

      if (Math.abs(this.wheelDelta) < 32 || isCoolingDown) return;

      this.wheelLastStepAt = now;
      this.wheelLockUntil = now + this.scrollStepWindow;
      this.wheelDelta = 0;
      this.navigateTo(this.currentIndex + direction);
    }

    scrollToPinnedIndex(index) {
      if (!this.isScrollPinActive()) return;
      const bounds = this.pinnedScrollBounds();
      if (!bounds || bounds.distance <= 0) return;
      const lastIndex = this.slides.length - 1;
      const progress = lastIndex > 0 ? index / lastIndex : 0;
      const target = bounds.start + bounds.distance * progress;
      this.pinInternalTarget = target;
      window.scrollTo({
        top: target,
        behavior: "auto"
      });
    }

    navigateTo(index, smooth = true) {
      const safeIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
      if (this.isScrollPinActive()) this.pinApproach = 0;
      this.goTo(safeIndex, smooth);
      this.scrollToPinnedIndex(safeIndex);
    }

    handleScroll() {
      if (this.isDragging || this.isAnimating) return;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const index = this.nearestIndex();
        if (index !== this.currentIndex) this.setActive(index);
      });
    }

    isNonMobileLayout() {
      return this.nonMobileLayoutQuery?.matches ?? window.matchMedia("(min-width: 768px)").matches;
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery?.matches ?? false;
    }

    trackInlineStart() {
      if (!this.track) return 0;
      return Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    }

    trackInlineEnd() {
      if (!this.track) return 0;
      return Number.parseFloat(getComputedStyle(this.track).paddingInlineEnd) || 0;
    }

    maxScroll() {
      if (!this.viewport) return 0;
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    }

    clampScroll(value) {
      return Math.min(this.maxScroll(), Math.max(0, value));
    }

    /**
     * @param {Element} slide
     * @param {number} index
     * @returns {"start" | "center" | "end"}
     */
    slideAlign(index) {
      if (index <= 0) return "start";
      if (index >= this.slides.length - 1) return "end";
      return "center";
    }

    slideScrollLeft(slide) {
      if (!slide || !this.viewport) return 0;
      const index = this.slides.indexOf(slide);
      const align = this.slideAlign(index);

      if (align === "end") {
        return this.clampScroll(
          slide.offsetLeft + slide.offsetWidth + this.trackInlineEnd() - this.viewport.clientWidth
        );
      }

      if (align === "center") {
        return this.clampScroll(slide.offsetLeft - (this.viewport.clientWidth - slide.offsetWidth) / 2);
      }

      return this.clampScroll(slide.offsetLeft - this.trackInlineStart());
    }

    nearestIndex() {
      if (!this.viewport || !this.slides.length) return 0;
      const scrollLeft = this.viewport.scrollLeft;
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(this.slideScrollLeft(slide) - scrollLeft);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });

      return nearest;
    }

    goTo(index, smooth = true) {
      if (!this.viewport || !this.slides.length) return;
      const safeIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
      const slide = this.slides[safeIndex];
      if (!this.isInteractiveLayout()) {
        this.setActive(safeIndex, false);
        return;
      }
      const left = this.slideScrollLeft(slide);
      this.setActive(safeIndex, false);
      this.animateScrollTo(left, smooth, () => this.playEntrance(slide));
      this.restartAutoplay();
    }

    animateScrollTo(left, smooth, onComplete = () => {}) {
      this.stopScrollAnimation();
      if (!this.viewport) return;

      const start = this.viewport.scrollLeft;
      const delta = left - start;
      if (!smooth || this.prefersReducedMotion() || Math.abs(delta) < 1) {
        this.viewport.scrollLeft = left;
        this.viewport.classList.remove("is-animating");
        this.isAnimating = false;
        onComplete();
        return;
      }

      const duration = Math.round(
        Math.min(
          ImmersiveFeatureCarousel.#maxDuration,
          Math.max(
            ImmersiveFeatureCarousel.#minDuration,
            420 + (Math.abs(delta) / Math.max(this.viewport.clientWidth, 1)) * 260
          )
        )
      );

      this.isAnimating = true;
      this.viewport.classList.add("is-animating");
      const startedAt = performance.now();

      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - (1 - progress) ** 3;
        this.viewport.scrollLeft = start + delta * eased;
        if (progress < 1) {
          this.scrollAnimation = requestAnimationFrame(step);
          return;
        }
        this.viewport.scrollLeft = left;
        this.stopScrollAnimation();
        onComplete();
      };

      this.scrollAnimation = requestAnimationFrame(step);
    }

    stopScrollAnimation() {
      cancelAnimationFrame(this.scrollAnimation);
      this.scrollAnimation = 0;
      this.isAnimating = false;
      this.viewport?.classList.remove("is-animating");
    }

    setActive(index, playEntrance = true) {
      const didChange = this.currentIndex !== index;
      if (didChange) this.resetEntrance();
      this.currentIndex = index;
      const interactive = this.isInteractiveLayout();

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", interactive && !active ? "true" : "false");
        slide.querySelector('story-video')?.setAttribute('data-media-active', String(!interactive || active));
        slide.querySelectorAll("button, a, input, select, textarea").forEach((control) => {
          if (!interactive || active) control.removeAttribute("tabindex");
          else control.setAttribute("tabindex", "-1");
        });
      });

      const copy = this.slides[index]?.querySelector("[data-carousel-copy]");
      if (this.activeCopy) {
        this.activeCopy.replaceChildren(this.createPresentationCopy(copy));
        if (didChange && !this.prefersReducedMotion()) {
          this.activeCopy.classList.remove("is-entering");
          void this.activeCopy.offsetWidth;
          this.activeCopy.classList.add("is-entering");
        }
      }
      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      this.syncCopyHeight();
      this.prioritizeNearbyMedia();
      if (playEntrance) this.playEntrance(this.slides[index]);
    }

    prioritizeNearbyMedia() {
      this.slides.forEach((slide, index) => {
        if (Math.abs(index - this.currentIndex) > 1) return;
        slide.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = "eager";
        });
      });
    }

    syncCopyHeight() {
      if (!this.activeCopy) return;
      if (!this.isInteractiveLayout()) {
        this.style.removeProperty("--immersive-feature-carousel-copy-height");
        this.sectionRoot?.classList.remove("is-sticky-active", "is-sticky-content-overflowing");
        return;
      }

      const copyWidth = this.activeCopy.clientWidth;
      if (!copyWidth) return;
      this.copyMeasure?.remove();
      this.copyMeasure = document.createElement("div");
      this.copyMeasure.className = "immersive-feature-carousel__active-copy immersive-feature-carousel__copy-measure";
      this.copyMeasure.style.inlineSize = `${copyWidth}px`;
      this.slides.forEach((slide) => {
        const copy = slide.querySelector("[data-carousel-copy]");
        if (copy) this.copyMeasure.append(this.createPresentationCopy(copy));
      });
      this.append(this.copyMeasure);
      const copyHeight = Math.ceil(Math.max(
        0,
        ...Array.from(this.copyMeasure.children).map((node) => node.getBoundingClientRect().height)
      ));
      this.copyMeasure.remove();
      this.copyMeasure = null;
      this.style.setProperty("--immersive-feature-carousel-copy-height", `${copyHeight}px`);
      this.syncStickyContentFit();
    }

    syncStickyContentFit() {
      if (!this.sectionRoot || !this.viewport || !this.footer || !this.isScrollPinActive()) {
        this.sectionRoot?.classList.remove("is-sticky-content-overflowing");
        this.style.removeProperty("--immersive-feature-carousel-sticky-media-clip");
        this.style.removeProperty("--immersive-feature-carousel-sticky-media-height");
        return;
      }

      const media = this.slides[this.currentIndex]?.querySelector(".immersive-feature-slide__media");
      const slideWidth = this.slides[this.currentIndex]?.getBoundingClientRect().width || 0;
      const aspectRatioParts = media ? getComputedStyle(media).aspectRatio.split("/").map(Number) : [];
      const aspectRatio = aspectRatioParts.length === 2
        && aspectRatioParts.every((value) => Number.isFinite(value) && value > 0)
        ? aspectRatioParts[0] / aspectRatioParts[1]
        : 2;
      const naturalMediaHeight = aspectRatio > 0 ? slideWidth / aspectRatio : 0;
      const footerHeight = this.footer.getBoundingClientRect().height;
      const contentHeight = naturalMediaHeight + footerHeight;
      const availableHeight = this.getBoundingClientRect().height;
      const contentDoesNotFit = contentHeight > availableHeight + 1;

      if (contentDoesNotFit) {
        const clip = Math.max(0, contentHeight - availableHeight);
        this.style.setProperty("--immersive-feature-carousel-sticky-media-clip", `${clip}px`);
        this.style.setProperty(
          "--immersive-feature-carousel-sticky-media-height",
          `${Math.max(0, naturalMediaHeight - clip)}px`
        );
      } else {
        this.style.removeProperty("--immersive-feature-carousel-sticky-media-clip");
        this.style.removeProperty("--immersive-feature-carousel-sticky-media-height");
      }

      this.sectionRoot.classList.toggle(
        "is-sticky-content-overflowing",
        contentDoesNotFit
      );
      this.syncStickyActiveState();
    }

    syncStickyActiveState(scrollY = window.scrollY, bounds = this.pinnedScrollBounds()) {
      if (!this.sectionRoot) return;

      const contentDoesNotFit = this.sectionRoot.classList.contains("is-sticky-content-overflowing");
      const isInsideStickyRange = Boolean(
        bounds
        && bounds.distance > 0
        && scrollY >= bounds.start - 1
        && scrollY <= bounds.start + bounds.distance + 1
      );
      this.sectionRoot.classList.toggle(
        "is-sticky-active",
        this.isScrollPinActive() && contentDoesNotFit && isInsideStickyRange
      );
    }

    createPresentationCopy(source) {
      if (!(source instanceof Element)) return document.createTextNode("");

      const copy = source.cloneNode(true);
      copy.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
        const presentationHeading = document.createElement("div");
        Array.from(heading.attributes).forEach((attribute) => {
          presentationHeading.setAttribute(attribute.name, attribute.value);
        });
        presentationHeading.replaceChildren(...heading.childNodes);
        heading.replaceWith(presentationHeading);
      });

      return copy;
    }

    usesEntranceReveal() {
      return Boolean(this.revealRoot?.classList.contains("is-enabled"));
    }

    canPlayEntrance() {
      if (this.usesEntranceReveal()) return this.revealRoot.classList.contains("is-visible");
      return this.isInViewport;
    }

    handleRevealChange(event) {
      if (event.target !== this.revealRoot) return;
      if (event.detail?.isVisible) {
        this.playEntrance(this.slides[this.currentIndex]);
        return;
      }
      this.resetEntrance();
    }

    resetEntrance() {
      this.entranceToken += 1;
      this.entranceSlide = null;
      cancelAnimationFrame(this.entranceFrame);
      this.entranceFrame = 0;
      this.slides.forEach((slide) => slide.classList.remove("is-scanning"));
    }

    /**
     * Decode the active scan overlay before the clip-path wipe runs.
     * @param {Element} slide
     * @returns {Promise<void>}
     */
    prepareScanMedia(slide) {
      if (!slide) return Promise.resolve();

      slide.querySelectorAll("img[loading='lazy']").forEach((image) => {
        image.loading = "eager";
      });

      const overlayImages = Array.from(slide.querySelectorAll("[data-carousel-scan] img"));
      if (!overlayImages.length) return Promise.resolve();

      return Promise.race([
        Promise.all(overlayImages.map((image) => {
          if (image.complete && image.naturalWidth > 0) return Promise.resolve();
          const loaded = new Promise((resolve) => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
          });
          if (typeof image.decode === "function") {
            return image.decode().then(() => undefined).catch(() => loaded);
          }
          return loaded;
        })),
        new Promise((resolve) => {
          window.setTimeout(resolve, 1500);
        })
      ]).then(() => undefined);
    }

    shouldPlayScan(slide) {
      if (!this.isNonMobileLayout()) return false;
      return Boolean(slide?.hasAttribute("data-carousel-has-scan"));
    }

    /**
     * Play the scan overlay only after the section is actually on screen.
     * Starting it while the reveal wrapper is still opacity 0 drops the
     * clip-path fill, so the first slide's background never appears.
     * Mobile and slides without a scan overlay skip this so the base image
     * does not flash.
     * @param {Element} [activeSlide]
     */
    async playEntrance(activeSlide) {
      if (!activeSlide || !this.canPlayEntrance() || !this.shouldPlayScan(activeSlide)) return;
      if (this.entranceSlide === activeSlide) return;

      const token = (this.entranceToken += 1);
      this.entranceSlide = activeSlide;
      cancelAnimationFrame(this.entranceFrame);
      this.entranceFrame = 0;
      this.slides.forEach((slide) => slide.classList.remove("is-scanning"));

      if (this.prefersReducedMotion()) {
        activeSlide.classList.add("is-scanning");
        return;
      }

      await this.prepareScanMedia(activeSlide);
      if (token !== this.entranceToken || !this.abortController || !this.canPlayEntrance()) return;
      if (!this.shouldPlayScan(activeSlide)) return;

      this.entranceFrame = requestAnimationFrame(() => {
        this.entranceFrame = requestAnimationFrame(() => {
          if (token !== this.entranceToken || !this.abortController) return;
          activeSlide.classList.add("is-scanning");
        });
      });
    }

    pauseAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = 0;
      clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = 0;
    }

    resumeAutoplay() {
      if (!this.isInteractiveLayout()) return;
      if (this.isScrollPinActive()) return;
      if (!this.autoplayEnabled || !this.isInViewport || document.hidden || this.matches(":hover") || this.matches(":focus-within")) return;
      this.pauseAutoplay();
      this.autoplayTimer = window.setTimeout(() => {
        const nextIndex = this.currentIndex >= this.slides.length - 1 ? 0 : this.currentIndex + 1;
        this.goTo(nextIndex);
      }, this.autoplayDelay);
    }

    restartAutoplay() {
      this.pauseAutoplay();
      this.resumeAutoplay();
    }

    deferAutoplayResume() {
      clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = window.setTimeout(() => this.resumeAutoplay(), 0);
    }
  }

  customElements.define("immersive-feature-carousel", ImmersiveFeatureCarousel);
}
